import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { validarFormulario } from "../utils/validators";
import { formatearFecha, esStockCritico } from "../utils/formatters";
import { createRepuesto, updateRepuesto, deleteRepuesto } from "../services/dataService";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import SectionHeader from "../components/ui/SectionHeader";
import { exportarPDF } from "../utils/exporters";

export default function ModuloInventario({ repuestos, setRepuestos, registrarMovimientoStock, registrarAuditoria, onNotify }) {
  const { puede, usuario } = useAuth(); // ✅ Agregamos 'usuario' aquí
  const [form, setForm] = useState(false);
  const [formData, setFormData] = useState({ 
    nombre: "", lote: "", proveedor: "", stock: "", stock_critico: "3", vencimiento: "" 
  });
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  
  // 🆕 Estado para controlar la cantidad a ajustar en cada fila independientemente
  const [ajusteCantidad, setAjusteCantidad] = useState({});

  const resetForm = () => {
    setFormData({ nombre: "", lote: "", proveedor: "", stock: "", stock_critico: "3", vencimiento: "" });
    setErrors({});
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!puede("crear") && !editing) { 
      onNotify?.("No tenés permiso para gestionar inventario", "error"); 
      return; 
    }
    if (!puede("gestionar_stock")) { 
      onNotify?.("No tenés permiso para modificar stock", "error"); 
      return; 
    }
    const errores = validarFormulario(formData, ['nombre', 'stock']);
    if (Object.keys(errores).length > 0) {
      setErrors(errores);
      onNotify?.("Verificá los campos obligatorios", "warning");
      return;
    }
    const data = { 
      ...formData, 
      stock: parseInt(formData.stock),
      stock_critico: parseInt(formData.stock_critico) || 3
    };
    try {
      if (editing) {
        await updateRepuesto({ ...data, id: editing.id });
        const viejoStock = repuestos.find(r => r.id === editing.id)?.stock || 0;
        const dif = data.stock - viejoStock;
        setRepuestos(prev => prev.map(r => r.id === editing.id ? { ...r, ...data } : r));
        if (dif !== 0 && registrarMovimientoStock) {
          registrarMovimientoStock(data.id || editing.id, data.nombre, dif, `Edición de repuesto`);
        }
        registrarAuditoria?.(`Repuesto "${data.nombre}" editado (stock: ${data.stock})`, "Inventario");
        onNotify?.("Repuesto actualizado", "success");
      } else {
        const saved = await createRepuesto(data);
        setRepuestos(prev => [...prev, saved]);
        if (registrarMovimientoStock) {
          registrarMovimientoStock(saved.id, saved.nombre, saved.stock, `Stock inicial`);
        }
        registrarAuditoria?.(`Repuesto "${saved.nombre}" agregado (stock: ${saved.stock})`, "Inventario");
        onNotify?.("Repuesto agregado al inventario", "success");
      }
    } catch (e) {
      onNotify?.(e.message, "error");
    }
    resetForm();
    setForm(false);
  };

  const handleEdit = (rep) => {
    if (!puede("editar") || !puede("gestionar_stock")) { 
      onNotify?.("No tenés permiso", "error"); 
      return; 
    }
    setFormData({ 
      ...rep, 
      stock: rep.stock.toString(), 
      stock_critico: (rep.stock_critico || 3).toString() 
    });
    setEditing(rep);
    setForm(true);
    setErrors({});
  };

  const handleDelete = async (id, nombre) => {
    if (!puede("eliminar")) { 
      onNotify?.("Solo administradores pueden eliminar", "error"); 
      return; 
    }
    if (window.confirm(`¿Eliminar "${nombre}" del inventario?`)) {
      try {
        await deleteRepuesto(id);
        setRepuestos(prev => prev.filter(r => r.id !== id));
        registrarAuditoria?.(`Repuesto "${nombre}" eliminado del inventario`, "Inventario");
        onNotify?.("Repuesto eliminado", "info");
      } catch (e) {
        onNotify?.(e.message, "error");
      }
    }
  };

  // 🆕 FUNCIÓN PARA AJUSTE RÁPIDO DE STOCK
  const ajustarStock = async (id, operacion) => {
    const cantidad = parseInt(ajusteCantidad[id]) || 0;
    if (cantidad <= 0) {
      onNotify?.("Ingresá una cantidad válida mayor a 0", "warning");
      return;
    }
  
    if (usuario?.rol === "Técnico" && operacion === 'sumar') {
      onNotify?.("⛔ Solo administradores pueden agregar stock al inventario", "error");
      return;
    }
  
    const repuesto = repuestos.find(r => r.id === id);
    if (!repuesto) return;

    const nuevoStock = operacion === 'sumar' ? repuesto.stock + cantidad : repuesto.stock - cantidad;
    if (nuevoStock < 0) {
      onNotify?.("Error: El stock no puede ser negativo", "error");
      return;
    }

    try {
      await updateRepuesto({ id, stock: nuevoStock });

      setRepuestos(prev => prev.map(r => r.id === id ? { ...r, stock: nuevoStock } : r));
    
      if (registrarMovimientoStock) {
        registrarMovimientoStock(
          repuesto.id,
          repuesto.nombre,
          operacion === 'sumar' ? cantidad : -cantidad,
          `Ajuste rápido - ${operacion === 'sumar' ? 'Reposición' : 'Uso en reparación'}`
        );
      }
      
      registrarAuditoria?.(`Stock de "${repuesto.nombre}" ${operacion === 'sumar' ? `+${cantidad}` : `-${cantidad}`} (ajuste rápido)`, "Inventario");
      setAjusteCantidad(prev => ({ ...prev, [id]: "" }));
      onNotify?.(`Stock ${operacion === 'sumar' ? 'agregado' : 'descontado'} correctamente`, "success");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
  };

  const hoy = new Date();
  const alertVenc = (v) => {
    if (!v) return "muted";
    const d = new Date(v);
    const dias = Math.ceil((d - hoy) / 86400000);
    if (dias < 0) return "danger";
    if (dias < 60) return "warning";
    return "success";
  };

  // ✅ ORDENAMIENTO: Filtrar y luego ordenar por ID descendente (Más nuevos primero)
  const filtrados = repuestos
    .filter(r =>
      r.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      r.lote?.toLowerCase().includes(search.toLowerCase()) ||
      r.proveedor?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (typeof a.id === 'number' && typeof b.id === 'number') return b.id - a.id;
      return b.id.localeCompare(a.id); 
    });

  return (
    <div>
      <SectionHeader 
        title="Inventario de repuestos" 
        subtitle={puede("gestionar_stock") ? "Gestioná stock y vencimientos" : "Visualización de inventario"} 
        action={
          <div className="flex gap-2">
            {puede("ver_auditorias") && (
              <Btn 
              variant="success" 
              small 
              onClick={() => exportarPDF(
                "Inventario de Repuestos", 
                ["Repuesto", "Lote", "Stock", "Vencimiento"], // Títulos de columnas
                repuestos.map(r => [r.nombre, r.lote, r.stock, r.vencimiento]), // Datos
                "inventario"
              )}
            >
              📥 Exportar PDF
            </Btn>
            )}
            {puede("crear") && puede("gestionar_stock") && (
              <Btn variant="primary" onClick={() => { setForm(f => !f); if (form) resetForm(); }}>
                {form ? "Cancelar" : "+ Agregar"}
              </Btn>
            )}
          </div>
        } 
      />

      {form && (
        <Card className="form-card">
          <p className="form-card__title">{editing ? "✏️ Editar repuesto" : "🆕 Nuevo repuesto"}</p>
          <div className="grid grid--2 gap-2 mb-2">
            <Input 
              value={formData.nombre} 
              onChange={e => { setFormData(p => ({ ...p, nombre: e.target.value })); setErrors(pr => ({ ...pr, nombre: null })); }} 
              placeholder="Nombre del repuesto *" 
              error={errors.nombre} 
              className="col-span-2" 
            />
            <Input 
              value={formData.lote} 
              onChange={e => setFormData(p => ({ ...p, lote: e.target.value }))} 
              placeholder="N° de lote" 
            />
            <Input 
              value={formData.proveedor} 
              onChange={e => setFormData(p => ({ ...p, proveedor: e.target.value }))} 
              placeholder="Proveedor" 
            />
            <Input 
              value={formData.stock} 
              onChange={e => { setFormData(p => ({ ...p, stock: e.target.value })); setErrors(pr => ({ ...pr, stock: null })); }} 
              placeholder="Stock Inicial (u.) *" 
              type="number" 
              error={errors.stock} 
            />
            <Input 
              value={formData.stock_critico} 
              onChange={e => setFormData(p => ({ ...p, stock_critico: e.target.value }))} 
              placeholder="Stock Crítico Alerta" 
              type="number" 
            />
            <Input 
              value={formData.vencimiento} 
              onChange={e => setFormData(p => ({ ...p, vencimiento: e.target.value }))} 
              type="date" 
              className="col-span-2" 
            />
          </div>
          <div className="flex gap-2">
            <Btn variant="primary" onClick={handleSubmit}>{editing ? "Actualizar" : "Agregar"}</Btn>
            <Btn variant="default" onClick={() => { setForm(false); resetForm(); }}>Cancelar</Btn>
          </div>
        </Card>
      )}

      <Input 
        value={search} 
        onChange={e => setSearch(e.target.value)} 
        placeholder="🔍 Buscar repuesto, lote o proveedor..." 
        className="mb-3" 
      />

      <div className="grid gap-2">
        {filtrados.map(r => {
          const critStock = esStockCritico(r.stock, r.stock_critico);
          const vcol = alertVenc(r.vencimiento);
          const diasVenc = r.vencimiento ? Math.ceil((new Date(r.vencimiento) - hoy) / 86400000) : null;
          return (
            <Card key={r.id} className={`repuesto-card ${critStock ? "repuesto-card--critical" : ""}`}>
              
              {/* Parte Superior: Info del producto */}
              <div className="flex justify-between items-center flex-wrap gap-3 mb-3 border-b pb-3">
                <div>
                  <strong className="text-lg">{r.nombre}</strong>
                  <p className="text-sm text-muted mt-0.5">Lote {r.lote || "—"} · {r.proveedor || "—"}</p>
                </div>
                <div className="flex gap-4 items-center flex-wrap">
                  <div className="text-right">
                    <p className={`text-xl font-bold ${critStock ? "text-danger" : "text-primary"}`}>{r.stock} u.</p>
                    {critStock && <Badge color="danger">⚠ Stock Crítico (≤{r.stock_critico})</Badge>}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted">Vence</p>
                    <Badge color={vcol}>
                      {r.vencimiento ? formatearFecha(r.vencimiento) : "—"}
                      {diasVenc !== null && diasVenc < 60 && diasVenc >= 0 && ` (${diasVenc} días)`}
                    </Badge>
                  </div>
                  {(puede("editar") || puede("eliminar")) && puede("gestionar_stock") && (
                    <div className="flex gap-1 ml-4">
                      {puede("editar") && <Btn small variant="default" onClick={() => handleEdit(r)}>✏️</Btn>}
                      {puede("eliminar") && <Btn small variant="danger" onClick={() => handleDelete(r.id, r.nombre)}>🗑️</Btn>}
                    </div>
                  )}
                </div>
              </div>

              {/* 🆕 Parte Inferior: Ajuste Rápido de Stock - Visible para técnicos y admins */}
              {(puede("gestionar_stock") || usuario?.rol === "Técnico") && (
                <div className="bg-surface p-2 rounded-lg flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-muted uppercase">⚡ Movimiento Rápido</span>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number" 
                      placeholder="Cant." 
                      className="w-16 p-1 text-sm"
                      value={ajusteCantidad[r.id] || ""}
                      onChange={(e) => setAjusteCantidad(prev => ({ ...prev, [r.id]: e.target.value }))}
                    />
                    {/* 🔒 Botón Restar - Visible para técnicos y admins */}
                    <Btn small variant="danger" onClick={() => ajustarStock(r.id, 'restar')} title="Restar (Usar)">
                      - Usar
                    </Btn>
                    {/* 🔒 Botón Sumar - Solo para admins */}
                    {puede("gestionar_stock") && (
                      <Btn small variant="success" onClick={() => ajustarStock(r.id, 'sumar')} title="Sumar (Reponer)">
                        + Agregar
                      </Btn>
                    )}
                  </div>
                </div>
              )}

            </Card>
          );
        })}
        {filtrados.length === 0 && <p className="text-muted text-center py-5">No se encontraron repuestos</p>}
      </div>
    </div>
  );
}